import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  SearchResultsGlobalStore,
  type ContentDetailsParams,
  type ObjectResultsTableColumnId,
  type SearchResultItem,
} from '@bizzdesign/data-access-search';
import { type SearchSortInput } from '@bizzdesign/sdk-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { type BDMenuItem } from '@bizzdesign/shared/models';
import {
  ApplyMenuItemPermissionsPipe,
  AttributeValueFormatPipe,
  TimeAgoPipe,
  TranslateMenuItemsPipe,
} from '@bizzdesign/shared/pipes';
import {
  baseRoutes,
  buildSearchResultObjectMenuOptions,
  copyValueToClipboard,
} from '@bizzdesign/shared/utils';
import { BdMenuItemComponent } from '@bizzdesign/ui-menu';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import type { Table } from 'primeng/table';
import { type TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { ManageObjectColumnsDialogComponent } from '../manage-object-columns-dialog/manage-object-columns-dialog.component';
import { ObjectResultsColumnSizing } from './object-results-column-sizing';
import { createObjectResultsAttributeValues } from '../../utils/object-results-attribute-values.utils';
import {
  createColumnsFromStoredState,
  hideColumn,
  moveColumn,
  OBJECT_RESULTS_SYSTEM_COLUMN_CATALOGUE,
  toggleColumnPin,
  toStoredColumnState,
  type ColumnMoveDirection,
  type ObjectResultsColumn,
  type ObjectResultsColumnCatalogue,
} from '../../utils/object-results-columns.utils';

const DOUBLE_CLICK_DELAY_MS = 250;

type SortableColumnId = 'type' | 'title';

/**
 * Presentation options that stay fixed for a given host of the results table.
 *
 * @property showHeader - Whether the column header row is rendered.
 * @property showSelectionColumn - Whether the leading checkbox column is shown.
 * @property scrollable - Whether the table body scrolls inside the container.
 */
export type ObjectResultsTableLayout = {
  readonly showHeader?: boolean;
  readonly showSelectionColumn?: boolean;
  readonly scrollable?: boolean;
};

/**
 * Which rows are selected and which row the details drawer is showing.
 *
 * @property isSelectionMode - Whether the surrounding view is selecting objects.
 * @property selectedItems - Items currently selected.
 * @property activeItemId - Item shown in the details drawer, empty when none is.
 */
export type ObjectResultsTableSelection = {
  readonly isSelectionMode?: boolean;
  readonly selectedItems?: SearchResultItem[];
  readonly activeItemId?: string;
};

/**
 * Whether the caller may run the workspace actions offered per row.
 *
 * @property actionDisabled - Whether those actions are disabled.
 * @property noAccessTooltip - Tooltip explaining why they are disabled.
 */
export type ManageWorkspaceAccess = {
  readonly actionDisabled?: boolean;
  readonly noAccessTooltip?: string;
};

const DEFAULT_LAYOUT: ObjectResultsTableLayout = {
  showHeader: false,
  showSelectionColumn: true,
  scrollable: false,
};

const NO_ITEMS: SearchResultItem[] = [];

const OBJECT_RESULTS_COLUMN_SORT_BY: Record<
  SortableColumnId,
  SearchSortInput['by']
> = {
  type: 'TYPE',
  title: 'NAME',
};

@Component({
  selector: 'bd-object-search-results-table',
  imports: [
    AsyncPipe,
    NgClass,
    FormsModule,
    RouterLink,
    SharedI18nModule,
    TableModule,
    CheckboxModule,
    ButtonModule,
    TooltipModule,
    TieredMenuModule,
    BdMenuItemComponent,
    ManageObjectColumnsDialogComponent,
    ApplyMenuItemPermissionsPipe,
    TranslateMenuItemsPipe,
    TimeAgoPipe,
    AttributeValueFormatPipe,
  ],
  templateUrl: './object-search-results-table.component.html',
  styleUrl: './object-search-results-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectSearchResultsTableComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchResultsGlobalStore = inject(SearchResultsGlobalStore);
  private readonly table = viewChild<Table<SearchResultItem>>('resultsTable');

  readonly items = input.required<SearchResultItem[]>();
  readonly layout = input<ObjectResultsTableLayout>(DEFAULT_LAYOUT);
  readonly selection = input<ObjectResultsTableSelection>({});
  readonly manageWorkspaceAccess = input<ManageWorkspaceAccess>({});
  readonly currentSort = input<SearchSortInput | undefined>(undefined);
  readonly columnCatalogue = input<ObjectResultsColumnCatalogue>(
    OBJECT_RESULTS_SYSTEM_COLUMN_CATALOGUE,
  );

  readonly openSearchResultItem = output<SearchResultItem>();
  readonly deleteSearchResultItem = output<SearchResultItem>();
  readonly openContentDetails = output<ContentDetailsParams>();
  readonly selectedItemsChange = output<SearchResultItem[]>();
  readonly selectAllChange = output<boolean>();
  readonly itemSelectionToggled = output<{
    shiftKey: boolean;
    itemId: SearchResultItem['id'];
  }>();
  readonly sortChange = output<SearchSortInput | undefined>();

  // Unwrapped one field at a time: a fresh group object from the caller then
  // only re-runs these, not the per-row computeds built from their values.
  readonly showHeader = computed(() => this.layout().showHeader ?? false);
  readonly showSelectionColumn = computed(
    () => this.layout().showSelectionColumn ?? true,
  );
  readonly scrollable = computed(() => this.layout().scrollable ?? false);
  readonly isSelectionMode = computed(
    () => this.selection().isSelectionMode ?? false,
  );
  readonly selectedItems = computed(
    () => this.selection().selectedItems ?? NO_ITEMS,
  );
  readonly activeItemId = computed(() => this.selection().activeItemId ?? '');
  readonly manageWorkspaceActionDisabled = computed(
    () => this.manageWorkspaceAccess().actionDisabled ?? false,
  );
  readonly manageWorkspaceNoAccessTooltip = computed(
    () => this.manageWorkspaceAccess().noAccessTooltip ?? '',
  );

  readonly manageColumnsDialogVisible = signal(false);
  readonly columns = computed(() =>
    createColumnsFromStoredState(
      this.searchResultsGlobalStore.objectResultsTableColumns(),
      this.columnCatalogue(),
    ),
  );

  readonly attributeValuesByItemId = computed(() =>
    createObjectResultsAttributeValues(this.items(), this.columnCatalogue()),
  );
  readonly hasPinnedColumns = computed(() =>
    this.columns().some((column) => column.pinned),
  );
  readonly sizing = new ObjectResultsColumnSizing({
    columns: this.columns,
    showSelectionColumn: this.showSelectionColumn,
    table: this.table,
    resizedColumnWidths:
      this.searchResultsGlobalStore.objectResultsTableColumnWidths,
    setColumnWidth: (columnId, width) =>
      this.searchResultsGlobalStore.updateObjectResultsTableColumnWidth(
        columnId,
        width,
      ),
  });

  readonly tableStyleClass = computed(() =>
    [
      'bd-object-search-results-table',
      this.scrollable() ? 'bd-scrollable' : '',
      this.hasPinnedColumns() ? 'bd-has-pinned-columns' : '',
    ]
      .filter(Boolean)
      .join(' '),
  );
  readonly columnMenuItemsById = computed(() => {
    const columns = this.columns();
    const map = new Map<ObjectResultsColumn['id'], BDMenuItem[]>();
    for (const column of columns) {
      const columnsInGroup = columns.filter(
        ({ pinned }) => pinned === column.pinned,
      );
      const columnIndex = columnsInGroup.findIndex(
        ({ id }) => id === column.id,
      );
      const sortBy = this.getSortBy(column.id);
      map.set(column.id, [
        {
          id: 'sort-ascending',
          labelKey: 'searchResults.sortAscending',
          tScope: 'featureSearch',
          icon: 'ui-arrow-up',
          disabled: sortBy === undefined,
          command: () => this.sortColumn(column.id, 'ASC'),
        },
        {
          id: 'sort-descending',
          labelKey: 'searchResults.sortDescending',
          tScope: 'featureSearch',
          icon: 'ui-arrow-down',
          disabled: sortBy === undefined,
          command: () => this.sortColumn(column.id, 'DESC'),
        },
        {
          separator: true,
        },
        {
          id: 'move-left',
          labelKey: 'searchResults.moveColumnLeft',
          tScope: 'featureSearch',
          icon: 'ui-arrow-left',
          disabled: columnIndex === 0,
          command: () => this.moveColumn(column.id, 'left'),
        },
        {
          id: 'move-right',
          labelKey: 'searchResults.moveColumnRight',
          tScope: 'featureSearch',
          icon: 'ui-arrow-right',
          disabled: columnIndex === columnsInGroup.length - 1,
          command: () => this.moveColumn(column.id, 'right'),
        },
        {
          separator: true,
        },
        {
          id: column.pinned ? 'unpin-column' : 'pin-column',
          labelKey: column.pinned
            ? 'searchResults.unpinColumn'
            : 'searchResults.pinColumn',
          tScope: 'featureSearch',
          icon: column.pinned ? 'ui-pin-solid' : 'ui-pin',
          command: () => this.toggleColumnPin(column.id),
        },
        {
          id: 'hide-column',
          labelKey: 'searchResults.hideColumn',
          tScope: 'featureSearch',
          icon: 'ui-hidden',
          disabled: columns.length === 1 || !column.canHide,
          command: () => this.hideColumn(column.id),
        },
        {
          separator: true,
        },
        {
          id: 'manage-columns',
          labelKey: 'searchResults.manageColumns',
          tScope: 'featureSearch',
          command: () => this.openManageColumnsDialog(),
        },
        {
          id: 'reset-table',
          labelKey: 'searchResults.resetTable',
          tScope: 'featureSearch',
          command: () => this.resetTable(),
        },
      ]);
    }
    return map;
  });

  readonly selectableItems = computed(() =>
    this.items().filter((item) => !item.disabled && !item.deleted),
  );

  readonly selectedSelectableItems = computed(() => {
    const selectedItemIds = new Set(
      this.selectedItems().map((item) => item.id),
    );
    return this.selectableItems().filter((item) =>
      selectedItemIds.has(item.id),
    );
  });

  readonly selectAllChecked = computed(() => {
    const selectableItems = this.selectableItems();
    return (
      selectableItems.length > 0 &&
      this.selectedSelectableItems().length === selectableItems.length
    );
  });

  readonly selectAllIndeterminate = computed(() => {
    const selectableItems = this.selectableItems();
    const selectedSelectableItems = this.selectedSelectableItems().length;
    return (
      selectedSelectableItems > 0 &&
      selectedSelectableItems < selectableItems.length
    );
  });

  readonly menuItemsById = computed(() => {
    const manageWorkspaceActionDisabled = this.manageWorkspaceActionDisabled();
    const manageWorkspaceNoAccessTooltip =
      this.manageWorkspaceNoAccessTooltip();
    const map = new Map<SearchResultItem['id'], BDMenuItem[]>();
    for (const item of this.items()) {
      const actionDisabled = item.deleted || manageWorkspaceActionDisabled;
      map.set(
        item.id,
        buildSearchResultObjectMenuOptions(
          {
            onCopyLink: () => this.onCopyLinkClick(item),
            onAddTo: () => this.openSearchResultItem.emit(item),
            onDelete: () => this.deleteSearchResultItem.emit(item),
            onSeeDetails: () => this.openContentDetailsDrawer(item),
          },
          {
            disabled: actionDisabled,
            disabledTooltip: manageWorkspaceNoAccessTooltip,
            includeDelete: item.externalId === undefined,
          },
        ),
      );
    }
    return map;
  });

  private doubleClick = false;
  private doubleClickTimer: ReturnType<typeof setTimeout> | undefined =
    undefined;
  private openMenu: TieredMenu | undefined = undefined;
  constructor() {
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.doubleClickTimer);
      this.openMenu?.hide();
    });
  }

  isRowActive(item: SearchResultItem): boolean {
    return item.id === this.activeItemId();
  }

  isRowFocusable(item: SearchResultItem): boolean {
    return !item.deleted && !item.disabled;
  }

  itemTooltipKey(
    item: SearchResultItem,
  ): 'deletedItemTooltip' | 'itemDisabledTooltip' | undefined {
    if (item.deleted) {
      return 'deletedItemTooltip';
    }

    if (item.disabled && this.isSelectionMode()) {
      return 'itemDisabledTooltip';
    }

    return undefined;
  }

  onRowClick(event: MouseEvent, item: SearchResultItem): void {
    if (
      this.isContentDetailsInteractionBlocked(item) ||
      this.isFromNestedInteractiveTarget(event)
    ) {
      return;
    }
    this.onItemClick(item);
  }

  onRowDoubleClick(event: MouseEvent, item: SearchResultItem): void {
    if (
      this.isRowInteractionBlocked(item) ||
      this.isFromNestedInteractiveTarget(event)
    ) {
      return;
    }
    this.onItemDoubleClick(item);
    event.stopPropagation();
  }

  onRowKeyDown(event: Event, item: SearchResultItem): void {
    if (
      this.isRowInteractionBlocked(item) ||
      this.isFromNestedInteractiveTarget(event)
    ) {
      return;
    }
    this.onItemDoubleClick(item);
    event.preventDefault();
  }

  onCheckboxClick(event: Event, itemId: SearchResultItem['id']): void {
    event.stopPropagation();
    this.itemSelectionToggled.emit({
      shiftKey: 'shiftKey' in event && event.shiftKey === true,
      itemId,
    });
  }

  onSelectAllChange(checked: boolean): void {
    this.selectAllChange.emit(checked);
  }

  onSelectedItemsChange(newValue: SearchResultItem[]): void {
    this.selectedItemsChange.emit(newValue);
  }

  onMoreViewsClick(event: Event, item: SearchResultItem): void {
    this.openContentDetailsDrawer(item, true);
    event.stopPropagation();
  }

  onMoreActionsClick(event: Event, actionMenu: TieredMenu): void {
    event.stopPropagation();
    if (this.openMenu && this.openMenu !== actionMenu) {
      this.openMenu.hide();
    }
    actionMenu.toggle(event);
    this.openMenu = actionMenu.visible ? actionMenu : undefined;
  }

  openManageColumnsDialog(): void {
    this.manageColumnsDialogVisible.set(true);
  }

  onManageColumnsConfirm(columns: ObjectResultsColumn[]): void {
    this.updateColumns(columns);
    this.manageColumnsDialogVisible.set(false);
  }

  onManageColumnsDismiss(): void {
    this.manageColumnsDialogVisible.set(false);
  }

  onMenuItemClick(event: Event, item: BDMenuItem): void {
    if (item.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  moveColumn(
    columnId: ObjectResultsTableColumnId,
    direction: ColumnMoveDirection,
  ): void {
    this.updateColumns(moveColumn(this.columns(), columnId, direction));
  }

  toggleColumnPin(columnId: ObjectResultsTableColumnId): void {
    this.updateColumns(toggleColumnPin(this.columns(), columnId));
  }

  hideColumn(columnId: ObjectResultsTableColumnId): void {
    this.updateColumns(hideColumn(this.columns(), columnId));
  }

  resetTable(): void {
    this.searchResultsGlobalStore.resetObjectResultsTableLayout();
    this.sizing.clearPrimeTableResizeState();
    if (this.currentSort() !== undefined) {
      this.sortChange.emit(undefined);
    }
  }

  isSelectionColumnPinned(): boolean {
    return this.showSelectionColumn();
  }

  isActionsColumnPinned(): boolean {
    return true;
  }

  isColumnPinned(column: ObjectResultsColumn): boolean {
    return column.pinned;
  }

  private sortColumn(
    columnId: ObjectResultsColumn['id'],
    order: SearchSortInput['order'],
  ): void {
    const sortBy = this.getSortBy(columnId);
    if (sortBy === undefined) {
      return;
    }
    this.sortChange.emit({ by: sortBy, order });
  }

  private getSortBy(
    columnId: ObjectResultsColumn['id'],
  ): SearchSortInput['by'] | undefined {
    if (columnId === 'type' || columnId === 'title') {
      return OBJECT_RESULTS_COLUMN_SORT_BY[columnId];
    }
    return undefined;
  }

  private openContentDetailsDrawer(
    item: SearchResultItem,
    scrollToUsedIn: boolean = false,
  ): void {
    this.openContentDetails.emit({ item, scrollToUsedIn });
  }

  private onCopyLinkClick(item: SearchResultItem): void {
    const urlTree = this.router.createUrlTree([baseRoutes.home, 'search'], {
      queryParams: {
        version: '1.0.0',
        drawerObjectId: JSON.stringify(item.id),
      },
    });
    copyValueToClipboard(
      new URL(
        this.router.serializeUrl(urlTree),
        window.location.origin,
      ).toString(),
    );
  }

  private onItemClick(item: SearchResultItem): void {
    this.doubleClick = false;
    // One timer is shared by every row, so a click on another row must cancel it.
    clearTimeout(this.doubleClickTimer);
    this.doubleClickTimer = setTimeout(() => {
      if (!this.doubleClick) {
        this.openContentDetailsDrawer(item);
      }
    }, DOUBLE_CLICK_DELAY_MS);
  }

  private onItemDoubleClick(item: SearchResultItem): void {
    this.doubleClick = true;
    clearTimeout(this.doubleClickTimer);
    this.openSearchResultItem.emit(item);
  }

  private isRowInteractionBlocked(item: SearchResultItem): boolean {
    return (
      item.deleted || item.disabled || this.manageWorkspaceActionDisabled()
    );
  }

  private isContentDetailsInteractionBlocked(item: SearchResultItem): boolean {
    return item.deleted === true || item.disabled === true;
  }

  private isFromNestedInteractiveTarget(event: Event): boolean {
    if (!(event.target instanceof Element)) {
      return false;
    }

    const closestInteractive = event.target.closest(
      'button, a, input, select, textarea, .p-button, .p-checkbox',
    );

    return !!closestInteractive && closestInteractive !== event.currentTarget;
  }

  private updateColumns(columns: ObjectResultsColumn[]): void {
    this.searchResultsGlobalStore.updateObjectResultsTableColumns(
      toStoredColumnState(columns),
    );
  }
}
